import ContactsTable from '@/components/conversation-insights/contacts-table';
import { Card } from '@/components/ui/card';
import React, { useMemo } from 'react';
import { KeyInsightsResponse } from '@/hooks/useConversationsInsights';
import PieChartsForInsights from '@/components/conversation-insights/pie-charts';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import CallIcon from '../../public/icons/new-dashboard/phone-call-02.svg';
import TotalCallIcon from '../../public/icons/new-dashboard/phone-1.svg';
import EngagedCallIcon from '../../public/icons/new-dashboard/phone-call-01.svg';
import UniqueContactsIcon from '../../public/icons/new-dashboard/users-profiles-02.svg';
import AverageTimeIcon from '../../public/icons/new-dashboard/clock-01.svg';
import HightIntentIcon from '../../public/icons/new-dashboard/arrow-up-square-contained.svg';
import ModerateIntentIcon from '../../public/icons/new-dashboard/minus-square-contained.svg';
import NormalIntentIcon from '../../public/icons/new-dashboard/minus-square-contained-gray.svg';
import LowIntentIcon from '../../public/icons/new-dashboard/arrow-down-square-contained.svg';
import DownloadIcon from '../../public/icons/new-dashboard/download-02.svg';

type Props = {
  overview: any;
  intentSpread: any[];
  customers: any[];
  keyInsights: KeyInsightsResponse | null;
  objections: any[];
  loadingOverview: boolean;
  loadingIntentSpread: boolean;
  loadingCustomers: boolean;
  loadingKeyInsights: boolean;
  loadingObjections: boolean;
  handleDownloadReport: Function;
  handlePageChange: Function;
  page: any;
  handleIntentClick: Function;
  handleIntentRemove: Function;
  insightsFilters: any;
};

function minutesToHHMM(totalMinutes) {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = Math.round(totalMinutes % 60);
  return `${hours}:${String(minutes).padStart(2, '0')}`;
}

const Insights = ({
  overview,
  intentSpread,
  customers,
  keyInsights,
  objections,
  loadingOverview,
  loadingIntentSpread,
  loadingCustomers,
  loadingKeyInsights,
  loadingObjections,
  handleDownloadReport,
  handlePageChange,
  page,
  handleIntentClick,
  handleIntentRemove,
  insightsFilters,
}: Props) => {
  const {
    total_calls: totalCalls,
    contact_count: uniqueContacts,
    detailed_calls: engagedCalls,
    avg_call_duration: avgCallDuration,
  } = overview ?? {};

  const countsByIntent = useMemo(
    () =>
      intentSpread.reduce<Record<string, number>>((acc, { intent, count }) => {
        acc[intent] = parseInt(count, 10);
        return acc;
      }, {}),
    [intentSpread],
  );

  const cards = [
    { intentKey: 'HIGH_INTENT', label: 'High', Icon: HightIntentIcon },
    { intentKey: 'MODERATE', label: 'Moderate', Icon: ModerateIntentIcon },
    { intentKey: 'NEUTRAL', label: 'Neutral', Icon: NormalIntentIcon },
    { intentKey: 'LOW_INTENT', label: 'Low', Icon: LowIntentIcon },
  ] as const;

  return (
    <div className="p-6">
      <div>
        <div className="flex mb-3 gap-3">
          <CallIcon />
          <p className="font-bold">Call Overview</p>
        </div>

        {/* Overview Cards */}
        {!loadingOverview && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
            <Card className="p-4 flex gap-2 border border-[#E4E4E7]">
              <TotalCallIcon />
              <div className="flex flex-col items-start">
                <p className=" font-semibold text-xl text-title">
                  {totalCalls ?? 0}
                </p>
                <p className="text-sm text-paragraph">Total Calls</p>
              </div>
            </Card>
            <Card className="p-4 flex gap-2 border border-[#E4E4E7]">
              <UniqueContactsIcon />
              <div className="flex flex-col items-start">
                <p className=" font-semibold text-xl text-title">
                  {uniqueContacts ?? 0}
                </p>
                <p className="text-sm text-paragraph">Unique Contacts</p>
              </div>
            </Card>
            <Card className="p-4 flex gap-2 border border-[#E4E4E7]">
              <EngagedCallIcon />
              <div className="flex flex-col items-start">
                <p className=" font-semibold text-xl text-title">
                  {engagedCalls ?? 0}
                </p>
                <p className="text-sm text-paragraph">
                  Engaged Calls &gt; 2 mins
                </p>
              </div>
            </Card>
            <Card className="p-4 flex gap-2 border border-[#E4E4E7]">
              <AverageTimeIcon />
              <div className="flex flex-col items-start">
                <p className=" font-semibold text-xl text-title">
                  {minutesToHHMM(avgCallDuration ?? 0) ?? 0}
                </p>
                <p className="text-sm text-paragraph">Avg. Call Duration</p>
              </div>
            </Card>
          </div>
        )}

        {/* Skeletons */}
        {loadingOverview && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
            <Skeleton className="h-[76px] rounded-xl bg-gradient-to-r from-[#EDEDED] to-[#D7D7D7]" />
            <Skeleton className="h-[76px] rounded-xl bg-gradient-to-r from-[#EDEDED] to-[#D7D7D7]" />
            <Skeleton className="h-[76px] rounded-xl bg-gradient-to-r from-[#EDEDED] to-[#D7D7D7]" />
            <Skeleton className="h-[76px] rounded-xl bg-gradient-to-r from-[#EDEDED] to-[#D7D7D7]" />
          </div>
        )}
      </div>
      <div className="mt-6">
        <p className="font-bold mb-3">Buying Intent</p>
        {/* Intent Cards */}
        {!loadingIntentSpread && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
            {cards.map(({ intentKey, label, Icon }) => {
              const isSelected = insightsFilters?.intents?.includes(intentKey)
              return (
              <Card
                key={intentKey}
                className={cn("p-4 flex gap-2 border border-[#E4E4E7] cursor-pointer hover:bg-gray-50",{'bg-gray-200 hover:bg-gray-200': isSelected})}
                onClick={() => {
                  if(isSelected){
                    handleIntentRemove(intentKey);
                  } else {
                    handleIntentClick(intentKey);
                  }
                }}
              >
                <Icon />
                <div className="flex flex-col items-start">
                  <p className="font-semibold text-xl text-title">
                    {countsByIntent[intentKey] ?? 0}
                  </p>
                  <p className="text-sm text-paragraph">{label}</p>
                </div>
              </Card>
            )})}
          </div>
        )}

        {/* Skeletons */}
        {loadingIntentSpread && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
            <Skeleton className="h-[76px] rounded-xl bg-gradient-to-r from-[#EDEDED] to-[#D7D7D7]" />
            <Skeleton className="h-[76px] rounded-xl bg-gradient-to-r from-[#EDEDED] to-[#D7D7D7]" />
            <Skeleton className="h-[76px] rounded-xl bg-gradient-to-r from-[#EDEDED] to-[#D7D7D7]" />
            <Skeleton className="h-[76px] rounded-xl bg-gradient-to-r from-[#EDEDED] to-[#D7D7D7]" />
          </div>
        )}
      </div>

      {(keyInsights?.agent_pitch_gaps?.length > 0 ||
        keyInsights?.lead_concerns?.length > 0) && (
        <div className="mt-6">
          <p className="font-bold mb-3">Key Insights</p>
          {/* insights */}

          {/* Charts */}
          <div>
            <PieChartsForInsights
              loadingKeyInsights={loadingKeyInsights}
              agentPitchGaps={keyInsights?.agent_pitch_gaps ?? []}
              leadConcerns={keyInsights?.lead_concerns ?? []}
            />
          </div>
        </div>
      )}

      {customers?.length > 0 && (
        <div className="mt-6">
          <div className="flex items-center justify-between mb-3">
            <p className="text-lg font-semibold">Contacts</p>
            <button
              type="button"
              className="text-[#1D7BA7] flex items-center"
              onClick={() => {
                handleDownloadReport();
              }}
            >
              <DownloadIcon className="mr-2" /> Download Report
            </button>
          </div>
          {/* Table */}
          <ContactsTable
            customers={customers}
            page={page}
            handlePageChange={handlePageChange}
          />
          <div />
        </div>
      )}

      {customers?.length === 0 && page > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <p className="text-lg font-semibold">No more contacts</p>
          <Button variant="default" onClick={() => handlePageChange(1)}>
            Go back
          </Button>
        </div>
      )}
    </div>
  );
};

export default Insights;
